import { DatePicker, Descriptions, Select, Table, TableColumnsType } from "antd";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from "react-router-dom";
import { getSteps } from "../../../api/courses.api";
import { getSemesters } from "../../../api/semesters.api";
import { createSurvey, finalizeSurvey, updateSurvey } from "../../../api/surveyQuestion.api";
import useAuth from "../../../hooks/useAuth";
import useAxios from "../../../hooks/useAxios";
import useNotification from "../../../hooks/useNotification";
import useSeqItem from "../../../hooks/useSeqItem";
import { getCommonCodeLabel } from "../../../lib/store";
import { IGetSteps } from "../../../models/courses.model";
import { IGetSemesters } from "../../../models/semesters.model";
import { IGetSurvey, IGetSurveyTotal, ISurvey, ISurveyQuestionItem } from "../../../models/surveyQuestion.model";
import { RootState } from "../../../store/rootReducer";
import Button from "../../Button";
import ErrorResponse from "../../ErrorResponse";
import Loader from "../../Loader";
import ConfirmModal from "../../modals/ConfirmModal";
import SelectCampusModal from "../../modals/SelectCampusModal";
import Radio from "../../Radio";
import Tinymce from "../../Tinymce";
import SelectLevelModal from "./modals/SelectLevelModal";
import SelectQuestionModal from "./modals/SelectQuestionModal";
import SurveyAnswerModal from "./modals/SurveyAnswerModal";
import SurveyResult from "./SurveyResult";
import MultiCampusModal from "../../statistics/regular-test/modals/MultiCampusModal";

interface IProps {
  data?: ISurvey;
}

const SurveyEditor: FC<IProps> = ({
  data,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { auth, currentCampus, isDarkMode } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // HP0008 = 설문조사 유형
  const HP0008 = commonCodes.find(item => item.commonCode === 'HP0008')?.detailCode;
  const { raiseSeqItem, lowerSeqItem } = useSeqItem();
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetSemesters>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetSteps>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios<IGetSurvey>();
  const { data: submitRes2, isLoading: submitIsLoading2, error: submitError2, execute: submitExecute2 } = useAxios<IGetSurveyTotal>();

  const [title, setTitle] = useState('');
  const [semesterCode, setSemesterCode] = useState<number>();
  const [surveyType, setSurveyType] = useState<string>();
  const [srtDate, setSrtDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [openYN, setOpenYN] = useState('Y');
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [courseCodes, setCourseCodes] = useState<number[]>([]);
  const [content, setContent] = useState('');
  const [multiCampusVisible, setMultiCampusVisible] = useState(false);
  const [selectLevelVisible, setSelectLevelVisible] = useState(false);
  const [levels, setLevels] = useState<any>([]);
  const [selectQuestionVisible, setSelectQuestionVisible] = useState(false);
  const [questions, setQuestions] = useState<any>([]);
  const [surveyAnswerVisible, setSurveyAnswerVisible] = useState(false);
  const [questionCode, setQuestionCode] = useState<number>();
  const [isCreate, setIsCreate] = useState(true);
  const [surveyCode, setSurveyCode] = useState<number>();
  const [totalMember, setTotalMember] = useState<number>();
  const [completeMember, setCompleteMember] = useState<number>();
  const [confirmFinalizeVisible, setConfirmFinalizeVisible] = useState(false);
  const [showQuestions, setShowQuestions] = useState(true);
  const [decisionYN, setDecisionYN] = useState('N');

  useEffect(() => {
    execute(getSemesters());
    execute2(getSteps());
  }, []);

  useEffect(() => {
    if (res2 && !isLoading2) {
      let _levels: any = [];
      res2?.data?.forEach((item: any) => {
        _levels = _levels.concat(item.step);
      });
      setLevels(_levels);
    }
  }, [res2, isLoading2]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      successNotification(t('common.message.save1'));
      handleData(submitRes.data);
    }
  }, [submitRes, submitIsLoading]);

  useEffect(() => {
    if (submitRes2 && !submitIsLoading2) {
      successNotification(t('homepage.survey.message1'));
      setTotalMember(submitRes2.data?.total);
      setDecisionYN('Y');
    }
  }, [submitRes2, submitIsLoading2]);

  useEffect(() => {
    if (data) {
      handleData(data);
    }
  }, [data]);

  const handleData = (data: ISurvey) => {
    setTitle(data.title);
    setSemesterCode(data.semesterCode);
    setSurveyType(data.surveyType);
    setSrtDate(data.srtDate);
    setEndDate(data.endDate);
    setOpenYN(data.openYN);
    setCampusCodes(data.campusCodes);
    setCourseCodes(data.courseCodes);
    setContent(data.content);
    setQuestions(data.questions);
    setIsCreate(false);
    setSurveyCode(data.surveyCode);
    setTotalMember(data.totalMember);
    setCompleteMember(data.completeMember);
    setDecisionYN(data.decisionYN);
  }

  const getCampusNames = (campusCodes: string[]) => {
    const campusNames = campusCodes.map(code => {
      const campus = campusData.find(item => item.campusCode === code);
      return campus?.campusKorName;
    });

    return campusNames;
  }

  const getLevelNames = (courseCodes: number[]) => {
    const levelNames = courseCodes.map(code => {
      const level = levels.find((item: any) => item.courseCode === code);
      return level?.courseName;
    });

    return levelNames;
  }

  const onDeleteQuestion = (index: number) => {
    const _questions = [...questions];
    _questions.splice(index, 1);
    setQuestions(_questions);
  }

  const onSubmit = () => {
    if (!title) {
      return infoNotification(t('homepage.archive.message1'));
    }
    if (!semesterCode) {
      return infoNotification(t('homepage.survey.message2'));
    }
    if (!surveyType) {
      return infoNotification(t('homepage.survey.message3'));
    }
    if (!srtDate) {
      return infoNotification(t('homepage.survey.message4'));
    }
    if (!endDate) {
      return infoNotification(t('homepage.survey.message5'));
    }
    if (!content) {
      return infoNotification(t('homepage.survey.message6'));
    }
    if (!questions.length) {
      return infoNotification(t('homepage.survey.message7'));
    }

    const req: any = {
      title,
      semesterCode,
      surveyType,
      srtDate,
      endDate,
      openYN,
      campusCodes,
      courseCodes,
      content,
      questions,
    };

    if (isCreate) {
      submitExecute(createSurvey(req))
    } else {
      if (!surveyCode) return;
      submitExecute(updateSurvey(surveyCode, req));
    }
  }

  const onConfirm = () => {
    if (!surveyCode) return;
    submitExecute2(finalizeSurvey(surveyCode));
    setConfirmFinalizeVisible(false);
  }

  const columns: TableColumnsType<ISurveyQuestionItem> = [
    {
      title: t('common.column.category'),
      dataIndex: 'surveyCategory',
      key: 'surveyCategory',
      align: 'center',
      render: value => getCommonCodeLabel('HP0007', value, 'ko'),
    },
    {
      title: t('common.column.title'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('homepage.survey.column1'),
      dataIndex: 'questionType',
      key: 'questionType',
      align: 'center',
      render: value => getCommonCodeLabel('HP0006', value, 'ko'),
    },
    {
      title: t('common.button.detail'),
      dataIndex: 'questionCode',
      key: 'questionCode',
      align: 'center',
      render: value => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className='btn-basic mint'
            onClick={() => {
              setQuestionCode(value);
              setSurveyAnswerVisible(true);
            }}
            text={t('common.button.detail')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      ),
    },
    {
      title: t('homepage.survey.column4'),
      dataIndex: 'seq',
      key: 'seq',
      align: 'center',
      render: value => (
        <div className="order-sett__area">
          <a
            className={value === 1 ? 'btn-order up disabled' : 'btn-order up'}
            onClick={() => {
              if (value === 1) return;
              setQuestions(raiseSeqItem(questions, 'seq', value));
            }}
          />
          <a
            className={value === questions.length ? 'btn-order down disabled' : 'btn-order down'}
            onClick={() => {
              if (value === questions.length) return;
              setQuestions(lowerSeqItem(questions, 'seq', value));
            }}
          />
        </div>
      ),
    },
    {
      title: t('common.button.delete'),
      dataIndex: 'seq',
      key: 'seq',
      align: 'center',
      render: (value, record, index) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className='btn-basic gray'
            onClick={() => onDeleteQuestion(index)}
            text={t('common.button.delete')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      {isLoading || isLoading2 || submitIsLoading || submitIsLoading2 ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={submitError} />
      <ErrorResponse error={submitError2} />

      <div className="comm-bx__between">
        <h5>{t('homepage.survey.title3')}</h5>
      </div>
      <div className="comm-table__area rowType">
        <Descriptions
          bordered
          column={4}
          size="small"
          labelStyle={{ width: '12.5%' }}
          contentStyle={{ width: '12.5%' }}
        >
          <Descriptions.Item label={t('common.column.title')} span={3}>
            <input
              type="text"
              className="form-control"
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.semester')}>
            <Select
              className="w-100"
              placeholder={t('common.button.select')}
              value={semesterCode}
              onChange={value => setSemesterCode(value)}
              disabled={decisionYN === 'Y'}
            >
              {res?.data?.map((item, index) => (
                <Select.Option
                  key={index}
                  value={item.semesterCode}
                >
                  {item.semesterName}
                </Select.Option>
              ))}
            </Select>
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.survey.column1')}>
            <Select
              className="w-100"
              placeholder={t('common.button.select')}
              value={surveyType}
              onChange={value => setSurveyType(value)}
              disabled={decisionYN === 'Y'}
            >
              {HP0008?.map((item, index) => (
                <Select.Option
                  key={index}
                  value={item.detailCode}
                >
                  {item.korName}
                </Select.Option>
              ))}
            </Select>
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.survey.column2')}>
            <div className="date-picker__area">
              <DatePicker
                value={srtDate ? moment(srtDate) : undefined}
                onChange={e => {
                  if (e === null) setSrtDate('');
                  else setSrtDate(moment(e).format('YYYYMMDD'))
                }}
              />
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.survey.column3')}>
            <div className="date-picker__area">
              <DatePicker
                value={endDate ? moment(endDate) : undefined}
                onChange={e => {
                  if (e === null) setEndDate('');
                  else setEndDate(moment(e).format('YYYYMMDD'))
                }}
              />
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.notice.column1')}>
            <Radio
              checked={openYN === 'Y'}
              onChange={() => setOpenYN('Y')}
              text={t('common.option.yes')}
            />
            <Radio
              className="ml-4"
              checked={openYN === 'N'}
              onChange={() => setOpenYN('N')}
              text={t('common.option.no')}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('common.menu.campus')} span={4}>
            <div className="comm-btn__area">
              <Button
                className="btn-basic mint"
                onClick={() => setMultiCampusVisible(true)}
                text={t('event.education.button2')}
                disabled={decisionYN === 'Y'}
              />
              <div className="mt-1">
              {
                auth.hqYn === 'Y'
                  ? campusCodes.length
                    ? getCampusNames(campusCodes).join(', ')
                    : `(${t('event.education.text9')})`
                  : getCampusNames([currentCampus]).join('')
              }
              </div>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.archive.column4')} span={4}>
            <div className="comm-btn__area">
              <Button
                className="btn-basic mint"
                onClick={() => setSelectLevelVisible(true)}
                text={t('homepage.survey.button1')}
                disabled={decisionYN === 'Y'}
              />
              <div className="mt-1">
              {
                courseCodes.length
                  ? getLevelNames(courseCodes).join(', ')
                  : `(${t('event.education.text9')})`
              }
              </div>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.survey.column5')} span={4}>
            <Tinymce
              content={content}
              setContent={setContent}
            />
          </Descriptions.Item>
          {
            surveyCode
              ? (
                <>
                  <Descriptions.Item label={t('homepage.survey.column6')} span={2}>
                    {totalMember}{t('common.text.person')}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('homepage.survey.column7')} span={2}>
                    {completeMember}{t('common.text.person')}
                  </Descriptions.Item>
                </>
              )
              : null
          }
        </Descriptions>
      </div>

      {
        showQuestions
          ? (
            <>
              <div className="comm-bx__between">
                <div className="comm-btn__area">
                  <h5>{t('homepage.survey.title4')}</h5>
                </div>
                <div className="comm-btn__area right">
                  <Button
                    className="btn-basic pink-line"
                    onClick={() => setSelectQuestionVisible(true)}
                    text={t('homepage.survey.button2')}
                    disabled={decisionYN === 'Y'}
                  />
                  <Button
                    className="btn-basic pink-line"
                    onClick={() => setShowQuestions(false)}
                    text={t('homepage.survey.button3')}
                    disabled={isCreate}
                  />
                </div>
              </div>
              <div className="comm-table__area">
                <Table
                  columns={columns}
                  dataSource={questions}
                  pagination={{ pageSize: 99999 }}
                />
              </div>
            </>
            )
          : (
            <>
              <div className="row mt-5">
                <div className="col-6" style={{ position: 'relative', top: '10px' }}>
                  <h5>{t('homepage.survey.button3')}</h5>
                </div>
                <div className="col-6 text-right comm-btn__area right">
                  <Button
                    className="btn-basic pink-line"
                    onClick={() => setShowQuestions(true)}
                    text={t('homepage.survey.title4')}
                  />
                </div>
              </div>
              <div className="mt-2">
                <SurveyResult surveyCode={surveyCode} />
              </div>            
            </>
          )
      }

      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => history.push(`/homepage/survey${location.search}`)}
            text={t('common.button.list')}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink-line"
            onClick={() => setConfirmFinalizeVisible(true)}
            text={t('homepage.survey.button4')}
            disabled={isCreate || decisionYN === 'Y'}
          />
          <Button
            className="btn-basic pink"
            onClick={onSubmit}
            text={t('common.button.save')}
          />
        </div>
      </div>

      {multiCampusVisible &&
        <MultiCampusModal        
          visible={multiCampusVisible}
          setVisible={setMultiCampusVisible}
          campusCodes={campusCodes}
          setCampusCodes={setCampusCodes}
        />
      }
      {selectLevelVisible &&
        <SelectLevelModal
          visible={selectLevelVisible}
          setVisible={setSelectLevelVisible}
          courseCodes={courseCodes}
          setCourseCodes={setCourseCodes}
        />
      }
      {selectQuestionVisible &&
        <SelectQuestionModal
          visible={selectQuestionVisible}
          setVisible={setSelectQuestionVisible}
          questions={questions}
          setQuestions={setQuestions}
          campusCodes={campusCodes}
        />
      }
      {surveyAnswerVisible &&
        <SurveyAnswerModal
          visible={surveyAnswerVisible}
          setVisible={setSurveyAnswerVisible}
          questionCode={questionCode}
        />
      }
      {confirmFinalizeVisible &&
        <ConfirmModal
          visible={confirmFinalizeVisible}
          setVisible={setConfirmFinalizeVisible}
          onConfirm={onConfirm}
          titleText={t('homepage.survey.button4')}
          messageText={t('homepage.survey.text2')}
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
        />
      }      
    </div>
  );
}

export default SurveyEditor;
